import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, ApplicationConfig, DEFAULT_CURRENCY_CODE, importProvidersFrom } from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withComponentInputBinding } from "@angular/router";
import { NgIdleModule } from "@ng-idle/core";
import { provideStore } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { MAINTENANCE, PAYMENT_REQUEST, REGULARISATION_OFF } from "@shared/constants/route.constants";
import { reducers } from "@shared/state/core.reducer";
import { Brands, isPartner } from "@shared/utils/brand-utils";
import { KeycloakService } from "keycloak-angular";
import { trimStart } from "lodash";
import { MessageService } from "primeng/api";
import { environment } from "../environments/environment";
import { AppResource } from "./app.resource";
import { routes } from "./app.routes";
import { ErrorInterceptor } from "./shared/interceptor/error.interceptor";
import { HttpErrorInterceptor } from "./shared/interceptor/http-interceptor.interceptor";
import { JwtInterceptor } from "./shared/interceptor/jwt.interceptor";
import { MaintenanceInterceptor } from "./shared/interceptor/maintenance.interceptor";
import { provideTranslation } from "./shared/providers/translate.provider";
import {
  MsalBroadcastService,
  MsalInterceptor,
  MsalService,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from "@azure/msal-angular";
import { MSALInstanceFactory } from "./shared/auth/msal.instance";
import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalAuthenticationService } from "./shared/services/msal-authentication.service";
import { MSALInterceptorConfigFactory } from "./shared/interceptor/msal.interceptor";
import { ConditionalMsalInterceptor } from "@shared/interceptor/msal-conditional.interceptor";
import { ADVISOR_SSO_CTX } from "@shared/constants/msal.constants";
import { ADVISOR_SSO_ACTIVE, ADVISOR_SSO_PENDING } from "@shared/constants/localstorage.constants";
import { TRUE } from "@shared/constants/general.constants";
import { AdvisorConsultationContextInterceptor } from "@shared/interceptor/advisor-consultation-context.interceptor";

export function initResource(resource: AppResource) {
  return () => resource.load(environment.brand);
}

// Put here routes that should be accessible without authentication
export const allowAnonymous: string[] = [MAINTENANCE, REGULARISATION_OFF, PAYMENT_REQUEST];

export function initializePartnerAuth(keycloak: KeycloakService) {
  if (allowAnonymous.some(route => trimStart(window.location.pathname, "/").includes(route)) || isAdvisorSsoEntry()) {
    return () => Promise.resolve(); // Ne pas initialiser Keycloak
  }

  console.log(`Initializing Partner Auth for ${environment.brand}...`);
  let url = "";
  let realm = "";
  let clientId = "";
  let tokenEndpoint = "";

  switch (environment.brand) {
    case Brands.HM:
      url = environment.keycloak.url;
      realm = environment.keycloak.realm;
      clientId = environment.keycloak.clientId;
      tokenEndpoint = environment.keycloak.tokenEndpoint;
      break;
    case Brands.MGEN:
    case Brands.MNT:
      url = environment.passConnect.url;
      realm = environment.passConnect.realm;
      clientId = environment.passConnect.clientId;
      tokenEndpoint = environment.passConnect.tokenEndpoint;
      break;
    default:
      throw new Error(`Unsupported brand for Partner Auth: ${environment.brand}`);
  }

  console.log("Partner Auth URL:", url);
  console.log("Partner Auth Realm:", realm);
  console.log("Partner Auth Client ID:", clientId);
  console.log("Partner Auth Token Endpoint:", tokenEndpoint);

  return () =>
    keycloak
      .init({
        config: {
          url: url,
          realm: realm,
          clientId: clientId,
          oidcProvider: {
            authorization_endpoint: `${url}/realms/${realm}/protocol/openid-connect/auth`,
            token_endpoint: `${environment.apiConfig.uri}${tokenEndpoint}`,
          },
        },
        initOptions: {
          onLoad: "login-required",
          checkLoginIframe: false,
          pkceMethod: "S256",
        },
      })
      .then(() => {
        console.log("Logged in?", keycloak.isLoggedIn());
      });
}

export function initializeMsalAuth(instance: IPublicClientApplication, msalAuthService: MsalAuthenticationService) {
  return async () => {
    await instance.initialize();

    const isSsoFlow = msalAuthService.isAdvisorSsoBootstrapFlow();

    const response = await instance.handleRedirectPromise();

    // Hors flux SSO conseiller, on ne marque jamais la session comme SSO active
    if (!isSsoFlow) {
      return;
    }

    if (response?.account) {
      instance.setActiveAccount(response.account);
      msalAuthService.markActive();

      const decodedState = response.state ? decodeURIComponent(response.state) : "";

      // Autoriser uniquement une route interne absolue applicative
      // Exemples valides: /accueil, /contrat/xxx/details?x=1#a
      // Refusés: //contrat/..., http://..., https://...
      if (decodedState.startsWith("/") && !decodedState.startsWith("//")) {
        const safeUrl = new URL(decodedState, window.location.origin);
        if (safeUrl.origin === window.location.origin) {
          const finalUrl = `${safeUrl.pathname}${safeUrl.search}${safeUrl.hash}`;
          window.history.replaceState({}, "", finalUrl);
        }
      }

      return;
    }

    const accounts = instance.getAllAccounts();
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      msalAuthService.markActive();
    }
  };
}

function isMsalEnabled(): boolean {
  const enabled = environment.msalConfig?.enabled;
  return enabled.toLowerCase() === TRUE;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    AppResource,
    { provide: DEFAULT_CURRENCY_CODE, useValue: "EUR" },
    importProvidersFrom(NgIdleModule.forRoot(), TranslateModule.forRoot(provideTranslation())),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initResource,
      deps: [AppResource],
      multi: true,
    },
    provideStore(reducers),
    MessageService,
    ...(isMsalEnabled()
      ? [
          {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
          },
          {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
          },
          {
            provide: APP_INITIALIZER,
            useFactory: initializeMsalAuth,
            deps: [MSAL_INSTANCE, MsalAuthenticationService],
            multi: true,
          },
          {
            provide: HTTP_INTERCEPTORS,
            useClass: ConditionalMsalInterceptor,
            multi: true,
          },
          {
            provide: HTTP_INTERCEPTORS,
            useClass: AdvisorConsultationContextInterceptor,
            multi: true,
          },
          MsalService,
          MsalBroadcastService,
          MsalInterceptor,
        ]
      : []),
    ...(isPartner(environment)
      ? [
          KeycloakService,
          { provide: APP_INITIALIZER, useFactory: initializePartnerAuth, deps: [KeycloakService], multi: true },
        ]
      : []),
  ],
};

function isAdvisorSsoEntry(): boolean {
  const hasCtx = new URLSearchParams(window.location.search).has(ADVISOR_SSO_CTX);
  const hasPendingSso = sessionStorage.getItem(ADVISOR_SSO_PENDING) === TRUE;
  const hasActiveSso = sessionStorage.getItem(ADVISOR_SSO_ACTIVE) === TRUE;

  return isMsalEnabled() && (hasCtx || hasPendingSso || hasActiveSso);
}
